import { useState, useEffect, useMemo } from 'react';
import { useFormacao, type Formacao } from '@/context/FormacaoContext';
import { useFuncionariosFormacao } from '@/context/FuncionariosFormacaoContext';
import { useModalOverflow } from '@/hooks/useModalOverflow';
import { useModalAnimation } from '@/hooks/useModalAnimation';
import CertificadoUploadSection from './CertificadoUploadSection';

interface Props {
  formacaoPreSel?: Formacao | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const ESTADOS_PARTICIPACAO = ['Inscrito', 'A Frequentar', 'Concluído', 'Desistiu', 'Reprovado'];

export default function RegistarParticipacaoModal({ formacaoPreSel, onClose, onSuccess }: Props) {
  useModalOverflow();
  const { animating, handleClose } = useModalAnimation(onClose);
  const { formacoes, participantes, adicionarParticipante, saving } = useFormacao();
  const { funcionarios } = useFuncionariosFormacao();

  const [formacaoId, setFormacaoId] = useState(formacaoPreSel?.id ?? '');
  const [funcionarioId, setFuncionarioId] = useState('');
  const [estadoParticipacao, setEstadoParticipacao] = useState('Inscrito');
  const [notaFinal, setNotaFinal] = useState('');
  const [custo, setCusto] = useState<number | ''>('');
  const [certificadoEmitido, setCertificadoEmitido] = useState(false);
  const [novoParticipanteId, setNovoParticipanteId] = useState<string | null>(null);
  const [observacoes, setObservacoes] = useState('');
  const [searchFunc, setSearchFunc] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Funcionários já inscritos nesta formação
  const inscritosIds = useMemo(() => {
    if (!formacaoId) return new Set<string>();
    return new Set(participantes.filter((p) => p.formacao_id === formacaoId).map((p) => p.funcionario_id));
  }, [participantes, formacaoId]);

  const funcFiltrados = useMemo(() => {
    const q = searchFunc.toLowerCase();
    return funcionarios.filter((f) => {
      const matchSearch = q === '' || f.nome.toLowerCase().includes(q) || f.id.includes(q);
      return matchSearch && !inscritosIds.has(f.id);
    }).slice(0, 30);
  }, [funcionarios, searchFunc, inscritosIds]);

  const funcSelecionado = funcionarios.find((f) => f.id === funcionarioId);
  const formacaoSelecionada = formacoes.find((f) => f.id === formacaoId);

  useEffect(() => {
    if (formacaoPreSel) setFormacaoId(formacaoPreSel.id);
  }, [formacaoPreSel]);

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!formacaoId) errs.formacao = 'Selecione uma ação de formação';
    if (!funcionarioId) errs.funcionario = 'Selecione um funcionário';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    const func = funcionarios.find((f) => f.id === funcionarioId);
    const resultado = await adicionarParticipante({
      formacao_id: formacaoId,
      funcionario_id: funcionarioId,
      funcionario_nome: func?.nome ?? null,
      estado_participacao: estadoParticipacao,
      nota_final: notaFinal || null,
      certificado_emitido: certificadoEmitido,
      data_certificado: null,
      observacoes: observacoes || null,
      avaliacao_nota: null,
      avaliacao_comentario: null,
      avaliacao_data: null,
      certificado_url: null,
      custo: custo === '' ? null : Number(custo),
    });
    if (resultado) {
      if (certificadoEmitido) {
        // Fica no modal para permitir upload do certificado
        setNovoParticipanteId(resultado.id);
      } else {
        onSuccess?.();
        onClose();
      }
    }
  }

  // ── Ecrã de upload pós-registo ──────────────────────────────────────────
  if (novoParticipanteId) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center"
        style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      >
        <div className={`bg-white rounded-2xl w-full max-w-md mx-4 p-7 ${animating ? 'modal-panel-active' : 'modal-panel-enter'}`}>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-emerald-500 flex-shrink-0">
              <i className="ri-check-line text-white text-base" />
            </div>
            <div>
              <h2 className="text-[16px] font-bold text-zinc-900">Participação registada!</h2>
              <p className="text-[11px] text-zinc-400">Agora pode anexar o certificado (opcional)</p>
            </div>
          </div>

          <CertificadoUploadSection
            participanteId={novoParticipanteId}
            formacaoId={formacaoId}
            certificadoUrl={null}
            certificadoEmitido
            compact={false}
          />

          <div className="flex items-center justify-end gap-2.5 mt-5 pt-4 border-t border-zinc-100">
            <button
              type="button"
              onClick={() => { onSuccess?.(); handleClose(); }}
              className="whitespace-nowrap px-5 py-2.5 text-[13px] font-medium text-zinc-600 bg-zinc-100 rounded-lg hover:bg-zinc-200 transition-colors cursor-pointer"
            >
              Saltar
            </button>
            <button
              type="button"
              onClick={() => { onSuccess?.(); handleClose(); }}
              className="whitespace-nowrap flex items-center gap-2 px-5 py-2.5 text-[13px] font-semibold text-white bg-zinc-900 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-check-line text-sm" /></div>
              Concluir
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className={`bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto mx-4 ${animating ? 'modal-panel-active' : 'modal-panel-enter'}`}>
        {/* Header */}
        <div className="px-7 pt-6 pb-4 border-b border-zinc-100 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl z-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-900">
              <i className="ri-user-add-line text-white text-base" />
            </div>
            <div>
              <h2 className="text-[16px] font-bold text-zinc-900">Registar Participação</h2>
              <p className="text-[11px] text-zinc-400">Associar funcionário a uma ação de formação</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-7 py-5 space-y-4">
          {/* Seleção da Formação */}
          <div>
            <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">
              Ação de Formação <span className="text-rose-500">*</span>
            </label>
            <select
              value={formacaoId}
              onChange={(e) => { setFormacaoId(e.target.value); setFuncionarioId(''); }}
              disabled={!!formacaoPreSel}
              className={`w-full px-3.5 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-400 cursor-pointer disabled:opacity-60 ${errors.formacao ? 'border-rose-400' : 'border-zinc-200'}`}
            >
              <option value="">Selecionar ação...</option>
              {formacoes.map((f) => (
                <option key={f.id} value={f.id}>{f.nome}</option>
              ))}
            </select>
            {errors.formacao && <p className="text-[11px] text-rose-500 mt-1">{errors.formacao}</p>}
            {formacaoSelecionada && (
              <p className="text-[11px] text-zinc-400 mt-1">
                {formacaoSelecionada.entidade_formadora} · {formacaoSelecionada.duracao_horas}h · {formacaoSelecionada.estado}
              </p>
            )}
          </div>

          {/* Seleção do Funcionário */}
          <div>
            <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">
              Funcionário <span className="text-rose-500">*</span>
            </label>

            {funcSelecionado ? (
              <div className={`flex items-center gap-3 px-3.5 py-2.5 border rounded-lg ${errors.funcionario ? 'border-rose-400' : 'border-zinc-200'}`}>
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-zinc-100 flex-shrink-0">
                  <span className="text-[10px] font-bold text-zinc-600">
                    {funcSelecionado.nome.split(' ').filter((_, i, arr) => i === 0 || i === arr.length - 1).map((n) => n[0]).join('').toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold text-zinc-800">{funcSelecionado.nome}</p>
                  <p className="text-[11px] text-zinc-400">{funcSelecionado.carreira} · #{funcSelecionado.id}</p>
                </div>
                <button
                  type="button"
                  onClick={() => { setFuncionarioId(''); setSearchFunc(''); }}
                  className="whitespace-nowrap w-6 h-6 flex items-center justify-center text-zinc-400 hover:text-rose-500 cursor-pointer"
                >
                  <i className="ri-close-line text-sm" />
                </button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <div className="w-8 h-8 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none">
                    <i className="ri-search-line text-sm" />
                  </div>
                  <input
                    type="text"
                    placeholder="Pesquisar funcionário por nome ou número..."
                    value={searchFunc}
                    onChange={(e) => setSearchFunc(e.target.value)}
                    className={`w-full pl-9 pr-4 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400 ${errors.funcionario ? 'border-rose-400' : 'border-zinc-200'}`}
                  />
                </div>
                {searchFunc && (
                  <div className="mt-1 border border-zinc-100 rounded-lg overflow-hidden max-h-44 overflow-y-auto">
                    {funcFiltrados.length === 0 ? (
                      <p className="px-4 py-3 text-[12px] text-zinc-400">Nenhum funcionário encontrado ou já inscrito</p>
                    ) : funcFiltrados.map((f) => (
                      <button
                        key={f.id}
                        type="button"
                        onClick={() => { setFuncionarioId(f.id); setSearchFunc(''); }}
                        className="whitespace-nowrap w-full flex items-center gap-3 px-4 py-2.5 hover:bg-zinc-50 transition-colors cursor-pointer text-left border-b border-zinc-50 last:border-b-0"
                      >
                        <div className="w-7 h-7 flex items-center justify-center rounded-full bg-zinc-100 flex-shrink-0">
                          <span className="text-[10px] font-bold text-zinc-600">
                            {f.nome.split(' ').filter((_, i, arr) => i === 0 || i === arr.length - 1).map((n) => n[0]).join('').toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-[12px] font-semibold text-zinc-800 truncate">{f.nome}</p>
                          <p className="text-[10px] text-zinc-400">{f.carreira} · #{f.id}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
            {errors.funcionario && <p className="text-[11px] text-rose-500 mt-1">{errors.funcionario}</p>}
          </div>

          {/* Estado de participação */}
          <div>
            <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Estado de Participação</label>
            <select
              value={estadoParticipacao}
              onChange={(e) => setEstadoParticipacao(e.target.value)}
              className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-400 cursor-pointer"
            >
              {ESTADOS_PARTICIPACAO.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* Custo da formação por participante */}
          <div>
            <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">
              Custo da Formação (€) <span className="text-[10px] font-normal text-zinc-400">opcional</span>
            </label>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[13px] font-semibold text-zinc-400">€</span>
              <input
                type="number"
                min={0}
                step={0.01}
                value={custo}
                onChange={(e) => setCusto(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="Ex: 150"
                className="w-full pl-7 pr-4 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400"
              />
            </div>
            {formacaoSelecionada && (
              <p className="text-[11px] text-zinc-400 mt-1">
                Formação: {formacaoSelecionada.nome} · {formacaoSelecionada.duracao_horas}h
              </p>
            )}
          </div>

          {/* Nota final */}
          <div>
            <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Nota Final</label>
            <input
              type="text"
              value={notaFinal}
              onChange={(e) => setNotaFinal(e.target.value)}
              placeholder="Ex: Aprovado, 16 valores, Satisfaz..."
              className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400"
            />
          </div>

          {/* Certificado emitido */}
          <div className="space-y-3">
            <div className="flex items-center gap-3 py-1">
              <button
                type="button"
                onClick={() => setCertificadoEmitido((v) => !v)}
                className={`w-10 h-5 rounded-full transition-colors cursor-pointer relative flex-shrink-0 ${certificadoEmitido ? 'bg-emerald-500' : 'bg-zinc-200'}`}
              >
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${certificadoEmitido ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
              <label className="text-[13px] font-medium text-zinc-700 cursor-pointer" onClick={() => setCertificadoEmitido((v) => !v)}>
                Certificado emitido
              </label>
            </div>
            {certificadoEmitido && (
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-information-line text-sm text-emerald-600" />
                  </div>
                  <p className="text-[12px] font-semibold text-emerald-700">Upload do certificado</p>
                </div>
                <p className="text-[11px] text-emerald-600">
                  Após registar a participação, poderá fazer o upload do ficheiro do certificado diretamente nesta janela.
                </p>
              </div>
            )}
          </div>

          {/* Observações */}
          <div>
            <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Observações</label>
            <textarea
              rows={2}
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              maxLength={500}
              placeholder="Notas adicionais sobre esta participação..."
              className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400 resize-none"
            />
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-zinc-100">
            <button
              type="button"
              onClick={handleClose}
              className="whitespace-nowrap px-5 py-2.5 text-[13px] font-medium text-zinc-600 bg-zinc-100 rounded-lg hover:bg-zinc-200 transition-colors cursor-pointer"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="whitespace-nowrap flex items-center gap-2 px-5 py-2.5 text-[13px] font-semibold text-white bg-zinc-900 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer disabled:opacity-50"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-loader-4-line text-sm animate-spin" /></div>
                  A registar...
                </>
              ) : (
                <>
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-user-add-line text-sm" /></div>
                  Registar
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
